import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { FaArrowRight, FaChartLine, FaShieldAlt, FaRocket } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

// Move to index.css for caching
const styles = `
  @keyframes gradient {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  .animate-gradient {
    background-size: 200% auto;
    animation: gradient 3s linear infinite;
  }
  @keyframes css-pulse {
    0%, 100% { opacity: 0.1; transform: scale(1); }
    50% { opacity: 0.2; transform: scale(1.03); }
  }
`;

const Overview = () => {
  const navigate = useNavigate();

  // Memoized static data
  const features = useMemo(
    () => [
      {
        icon: <FaArrowRight className="text-2xl text-white" />,
        text: "Track every potential customer from website forms, chat widgets, or ad clicks",
        color: "from-pink-500 to-rose-500",
      },
      {
        icon: <FaShieldAlt className="text-2xl text-white" />,
        text: "Flags unusual activity instantly, so your team only sees real people",
        color: "from-orange-500 to-red-500",
      },
      {
        icon: <FaChartLine className="text-2xl text-white" />,
        text: "Send instant replies, follow-ups, or nurturing emails while you focus on closing deals",
        color: "from-blue-500 to-cyan-500",
      },
      {
        icon: <FaRocket className="text-2xl text-white" />,
        text: "Simple visual dashboard showing which ads, campaigns, or posts bring real results",
        color: "from-green-500 to-emerald-500",
      },
    ],
    []
  );

  const steps = useMemo(
    () => [
      { num: "01", label: "Ingest", desc: "Web, ads, CRM data streams" },
      {
        num: "02",
        label: "Process",
        desc: "Instantly know who’s ready to buy, and who’s just browsing",
      },
      {
        num: "03",
        label: "Prioritize",
        desc: "Stop guessing, start improving your marketing ROI",
      },
      {
        num: "04",
        label: "Automate",
        desc: "Connect WhatsApp, HubSpot, Mailchimp, or more",
      },
    ],
    []
  );

  // Respect reduced motion
  const prefersReducedMotion = useMemo(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // Simplified variants
  const containerVariants = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } },
  };

  const featureVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
  };

  const stepVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
  };

  const handleStartTrial = () => navigate("/waitlist");

  return (
    <section
      id="overview"
      className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 py-20"
    >
      {/* Background - reduced orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-xl [animation:css-pulse 4s ease-in-out infinite]" />
        <div className="absolute bottom-20 right-1/4 w-64 h-64 bg-pink-500/10 rounded-full blur-xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:1s]" />
      </div>

      {/* Grid - lighter opacity */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="relative container mx-auto px-6 max-w-6xl">
        {/* Header */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 mb-5"
          >
            <span className="text-sm font-semibold text-white/90">
              The Lead Symphony
            </span>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-black mb-4 text-white leading-tight">
            Stop losing leads,
            <br />
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Start understanding them.
            </span>
          </h2>

          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            One AI-powered system to capture, score, and act on leads from all your
            marketing channels in a clean dashboard.
          </p>
        </motion.div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Left: Features */}
          <Features features={features} prefersReducedMotion={prefersReducedMotion} />

          {/* Right: Process Flow */}
          <Steps steps={steps} prefersReducedMotion={prefersReducedMotion} />
        </div>

        {/* Bottom CTA */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative rounded-xl bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-white/10 p-8 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to transform your lead workflow?
          </h3>
          <p className="text-base text-slate-300 max-w-xl mx-auto mb-6">
            Join 2,000+ teams orchestrating their lead symphony with AI-powered
            intelligence.
          </p>
          <motion.button
            onClick={handleStartTrial}
            whileHover={prefersReducedMotion ? false : { scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            className="group relative px-6 py-3 rounded-lg font-semibold text-base bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white shadow-md"
            aria-label="Start your free trial"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            <span className="relative flex items-center gap-2 justify-center">
              Start Your Free Trial
              <svg
                className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </span>
          </motion.button>
        </motion.div>
      </div>

      <style>{styles}</style>
    </section>
  );
};

// Extracted Features component
const Features = React.memo(({ features, prefersReducedMotion }) => {
  const featureVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
  };

  return (
    <motion.div
      className="space-y-4"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {features.map((feature, idx) => (
        <motion.div
          key={idx}
          variants={featureVariants}
          transition={{ delay: idx * 0.1 }}
          className="group relative"
        >
          <div className="relative bg-white/5 rounded-xl p-5 border border-white/10 hover:border-white/20 transition-all">
            <div
              className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity rounded-xl`}
            />
            <div className="relative flex items-start gap-4">
              <motion.div
                className={`p-2.5 bg-gradient-to-br ${feature.color} rounded-lg shadow-md flex-shrink-0`}
                whileHover={prefersReducedMotion ? false : { scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {feature.icon}
              </motion.div>
              <p className="text-white text-sm">{feature.text}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
});

// Extracted Steps component
const Steps = React.memo(({ steps, prefersReducedMotion }) => {
  const stepVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
  };

  return (
    <motion.div
      className="space-y-4"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.1 }}
    >
      {steps.map((step, idx) => (
        <motion.div
          key={idx}
          variants={stepVariants}
          transition={{ delay: idx * 0.1 }}
          className="flex gap-5 items-start"
        >
          <motion.div
            className="relative flex-shrink-0 w-14 h-14 rounded-lg bg-gradient-to-br from-pink-500 via-purple-500 to-cyan-500 p-0.5"
            whileHover={prefersReducedMotion ? false : { scale: 1.1 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="absolute inset-0.5 rounded-md bg-slate-900 flex items-center justify-center">
              <span className="text-xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                {step.num}
              </span>
            </div>
          </motion.div>
          <div className="pt-2">
            <h4 className="text-lg font-semibold text-white mb-1">{step.label}</h4>
            <p className="text-slate-400 text-sm">{step.desc}</p>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
});

export default Overview;